import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { getDefaultFormattedLocalDate } from '@/lib/date';
import { generateUniqueVariableKey } from '@/core/cell';
import { SegmentedControl } from '@/components/segmented-control';
import { Toggle } from '@/components/form/toggle';
import { isNumericString } from '@/lib/utils/string';

import { useToastContext } from '../../components/toast';
import { Select } from '../../components/form/select';
import { Input } from '../../components/form/input';
import { VariableDefinition } from '../types';
import { FormField } from '../../components/form/form-field';
import {
  getDefaultValueForMultiTypeVariable,
  getVariableDefaultOption,
  isUniqueVariableKey,
} from '../utils';
import { InlineButton } from '../../components/button';
import { Icon } from '../../components/icon';
import { SortableItemSimple } from '../components/sortable-item';
import { EnumVariableDefinitionForm } from './enum-variable-definition';

import formStyles from '../../components/form/form.module.scss';
import styles from './edit-variable.module.scss';

interface VariableDefinitionFormProps {
  definition: VariableDefinition;
  existingKeys: VariableDefinition['key'][];
  onSubmit: (definition: VariableDefinition) => void;
}

const KindOptions: { value: VariableDefinition['kind']; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'enum', label: 'Select' },
  { value: 'date', label: 'Date' },
  { value: 'time_interval', label: 'Time Interval' },
];

export const VariableDefinitionForm = (props: VariableDefinitionFormProps) => {
  const { definition, existingKeys, onSubmit } = props;

  const addToast = useToastContext();
  const [key, setKey] = useState<VariableDefinition['key']>(definition.key);

  const handleKindChange = (value: VariableDefinition['kind']) => {
    switch (value) {
      case 'string':
        onSubmit({ key: definition.key, kind: value, defaultValue: '' });
        break;
      case 'number':
        onSubmit({ key: definition.key, kind: value, defaultValue: 0 });
        break;
      case 'boolean':
        onSubmit({ key: definition.key, kind: value, defaultValue: false });
        break;
      case 'enum':
        onSubmit({
          key: definition.key,
          kind: value,
          source: 'static',
          options: [{ value: '' }, { value: '' }, { value: '' }],
          defaultValue: '',
          queryOptions: { pipelineId: '', field: '' },
        });
        break;
      case 'date':
        onSubmit({
          key: definition.key,
          kind: value,
          defaultValue: getDefaultFormattedLocalDate(),
        });
        break;
      case 'time_interval':
        onSubmit({ key: definition.key, kind: value, defaultValue: 'month' });
        break;
    }
  };

  const handleKeyChange = () => {
    if (isUniqueVariableKey(key, existingKeys)) {
      return onSubmit({ ...definition, key });
    }

    const newKey = generateUniqueVariableKey(key, existingKeys);

    addToast({
      title: `Variable renamed to "${newKey}"`,
      content: () => `Variable names need to be unique. Name was changed to "${newKey}"`,
      kind: 'success',
    });

    onSubmit({ ...definition, key: newKey });
  };

  const convertOptionTypes = (options: { value: string | number }[]) => {
    const onlyNumericOptions = options.every(({ value }) => isNumericString(String(value)));

    return onlyNumericOptions
      ? options.map((option) => ({ value: Number(option.value) }))
      : options.map((option) => ({ value: String(option.value) }));
  };

  const handleOptionChange = (idx: number, value: string) => {
    if ('options' in definition) {
      const updatedOptions = definition.options.map((option, optionIdx) =>
        optionIdx === idx ? { ...option, value } : option,
      );

      const updatedDefinition = {
        ...definition,
        options: convertOptionTypes(updatedOptions),
      };

      onSubmit(
        updatedDefinition.kind === 'enum'
          ? {
              ...updatedDefinition,
              defaultValue: getDefaultValueForMultiTypeVariable(updatedDefinition),
            }
          : updatedDefinition,
      );
    }
  };

  const handleOptionRemove = (idx: number) => {
    if ('options' in definition) {
      onSubmit({
        ...definition,
        options: convertOptionTypes([
          ...definition.options.slice(0, idx),
          ...definition.options.slice(idx + 1),
        ]),
      });
    }
  };

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const onReorder = (fromIndex: number, toIndex: number) => {
    if ('options' in definition) {
      const [option] = definition.options.splice(fromIndex, 1);

      onSubmit({
        ...definition,
        options: [
          ...definition.options.slice(0, toIndex),
          option,
          ...definition.options.slice(toIndex),
        ],
      });
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    onSubmit({ ...definition, key });
  };

  const handleChangeEnumSource = (value: 'static' | 'query') => {
    if ('source' in definition) {
      onSubmit({ ...definition, source: value });
    }
  };

  return (
    <form className={classNames(formStyles.formHorizontal)} onSubmit={handleSubmit}>
      <div className={formStyles.formRow}>
        <FormField
          label="Name"
          error={isUniqueVariableKey(key, existingKeys) ? undefined : 'Name must be unique'}>
          <Input
            required
            type="text"
            placeholder="Enter variable name"
            value={key}
            onBlur={handleKeyChange}
            onChange={(event) => setKey(event.currentTarget.value)}
          />
        </FormField>
      </div>
      <div className={formStyles.formRow}>
        <FormField label="Type">
          <Select
            options={KindOptions}
            value={definition.kind === 'multiselect' ? 'enum' : definition.kind}
            onChange={handleKindChange}
          />
        </FormField>
      </div>
      {'source' in definition && (
        <>
          <div className={formStyles.sectionTitle}>Options</div>
          <SegmentedControl
            options={[
              {
                label: (
                  <>
                    <Icon name="List" size={14} />
                    List
                  </>
                ),
                // Undefined check can be removed once we validate exploration state with zod schema
                selected: definition.source === undefined || definition.source === 'static',
                onClick: () => handleChangeEnumSource('static'),
              },
              {
                label: (
                  <>
                    <Icon name="Database" size={14} />
                    Query
                  </>
                ),
                selected: definition.source === 'query',
                onClick: () => handleChangeEnumSource('query'),
              },
            ]}
          />

          {definition.source !== 'query' && (
            <>
              {definition.options.map((option, idx) => (
                <SortableItemSimple
                  className={classNames(styles.option)}
                  key={idx}
                  scope={'variable-enum-options'}
                  index={idx}
                  draggedIndex={draggedIndex}
                  setDraggedIndex={setDraggedIndex}
                  onReorder={onReorder}>
                  <Icon name={'DragHandle'} size={10} className={styles.optionDragHandle} />
                  <EnumOptionEditor
                    idx={idx}
                    value={String(option.value)}
                    onChange={(value) => handleOptionChange(idx, value)}
                    onRemove={() => handleOptionRemove(idx)}
                  />
                </SortableItemSimple>
              ))}

              <div>
                <InlineButton
                  onClick={() =>
                    onSubmit({
                      ...definition,
                      options: [
                        ...definition.options,
                        { value: getVariableDefaultOption(definition) },
                      ],
                    })
                  }>
                  <Icon name="Plus" size={15} /> Option
                </InlineButton>
              </div>
            </>
          )}

          {definition.source === 'query' && (
            <EnumVariableDefinitionForm definition={definition} onSubmit={onSubmit} />
          )}

          <div className={formStyles.formRow}>
            <Toggle
              checked={definition.kind === 'multiselect'}
              onChange={(checked) => {
                checked
                  ? props.onSubmit({
                      ...definition,
                      kind: 'multiselect',
                      defaultValue: [],
                    })
                  : props.onSubmit({
                      ...definition,
                      kind: 'enum',
                      defaultValue: getDefaultValueForMultiTypeVariable(definition),
                    });
              }}
              size="small">
              Allow multiple values
            </Toggle>
          </div>
        </>
      )}
    </form>
  );
};

interface EnumOptionEditorProps {
  idx: number;
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}

const EnumOptionEditor = (props: EnumOptionEditorProps) => {
  const [value, setValue] = useState<string>(props.value);

  useEffect(() => setValue(props.value), [props.value]);

  return (
    <FormField label={`Option ${props.idx + 1}`} onRemove={props.onRemove}>
      <Input
        required
        type="text"
        placeholder="Enter name"
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyUp={(e) => e.key === 'Enter' && props.onChange(value)}
        onBlur={(e) => props.onChange(e.currentTarget.value)}
      />
    </FormField>
  );
};
